import React, { useState, useEffect } from 'react';
import api from '../services/api';
import axios from 'axios'; // TODO: rimuovere, usare api
import { XMarkIcon } from '@heroicons/react/24/outline';

const SocietaNoleggioForm = ({ societa, garanzie, onSave, onCancel }) => {
  const isNew = !societa;
  const [formData, setFormData] = useState({
    nome: '',
    tipiClientiAccettati: [],
    accettaPrivatiRedditoUnico: false,
    idGaranzie: [],
    note: '',
    attivo: true
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // Inizializza il form con i dati della società se in modalità modifica
  useEffect(() => {
    if (societa) {
      setFormData({
        nome: societa.nome || '',
        // Converte la stringa separata da virgole in un array
        tipiClientiAccettati: societa.tipiClientiAccettati ? societa.tipiClientiAccettati.split(',') : [],
        // Ottieni gli ID delle garanzie associate
        idGaranzie: societa.garanzie ? societa.garanzie.map(g => g.id) : [],
        note: societa.note || '',
        attivo: societa.attivo !== undefined ? societa.attivo : true
      });
    }
  }, [societa]);

  // Gestione cambiamento campi form
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === 'tipiClienti') {
      // Gestione checkbox per tipi clienti
      const tipi = [...formData.tipiClientiAccettati];
      if (checked) {
        if (!tipi.includes(value)) {
          tipi.push(value);
        }
      } else {
        const index = tipi.indexOf(value);
        if (index !== -1) {
          tipi.splice(index, 1);
        }
      }
      setFormData(prev => ({
        ...prev,
        tipiClientiAccettati: tipi
      }));
    } else if (name === 'garanzia') {
      // Gestione checkbox per garanzie
      const garanzieSelezionate = [...formData.idGaranzie];
      const garanziaId = parseInt(value);
      
      if (checked) {
        if (!garanzieSelezionate.includes(garanziaId)) {
          garanzieSelezionate.push(garanziaId);
        }
      } else {
        const index = garanzieSelezionate.indexOf(garanziaId);
        if (index !== -1) {
          garanzieSelezionate.splice(index, 1);
        }
      }
      
      setFormData(prev => ({
        ...prev,
        idGaranzie: garanzieSelezionate
      }));
    } else {
      // Gestione normale per altri campi
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }

    // Pulisci l'errore quando l'utente modifica il campo
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  // Validazione form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.nome.trim()) {
      newErrors.nome = 'Il nome è obbligatorio';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Invio form
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    // Prepara i dati per l'invio
    const dataToSend = {
      ...formData,
      // Converte array in stringa separata da virgole
      tipiClientiAccettati: formData.tipiClientiAccettati.join(',')
    };

    try {
      if (isNew) {
        // Creazione nuova società
        const response = await api.post('/noleggio/societanoleggio', dataToSend);
        onSave(response.data, true);
      } else {
        // Aggiornamento società esistente
        const response = await api.put(`/noleggio/societanoleggio/${societa.id}`, dataToSend);
        onSave(response.data, false);
      }
    } catch (err) {
      console.error('Errore durante il salvataggio della società di noleggio:', err);
      if (err.response && err.response.data && err.response.data.message) {
        setErrors({ submit: err.response.data.message });
      } else {
        setErrors({ submit: 'Errore durante il salvataggio. Riprova più tardi.' });
      }
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        {/* Overlay scuro */}
        <div className="fixed inset-0 bg-black opacity-50" onClick={onCancel}></div>

        {/* Modale */}
        <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full z-10 relative">
          {/* Intestazione */}
          <div className="flex justify-between items-center border-b px-6 py-4">
            <h3 className="text-lg font-medium text-gray-900">
              {isNew ? 'Aggiungi Società di Noleggio' : 'Modifica Società di Noleggio'}
            </h3>
            <button
              onClick={onCancel}
              className="text-gray-400 hover:text-gray-500"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Contenuto form */}
          <div className="px-6 py-4">
            <form onSubmit={handleSubmit}>
              {/* Errore generale */}
              {errors.submit && (
                <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md border border-red-200">
                  {errors.submit}
                </div>
              )}

              {/* Nome società */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="nome">
                  Nome Società *
                </label>
                <input
                  type="text"
                  id="nome"
                  name="nome"
                  value={formData.nome}
                  onChange={handleChange}
                  className={`w-full p-2 border rounded-md ${errors.nome ? 'border-red-500' : 'border-gray-300'}`}
                  placeholder="Inserisci il nome della società"
                />
                {errors.nome && (
                  <p className="mt-1 text-sm text-red-600">{errors.nome}</p>
                )}
              </div>

              {/* Tipi di clienti accettati (checkbox) */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tipi di Clienti Accettati
                </label>
                <div className="space-y-2">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="tipoPrivati"
                      name="tipiClienti"
                      value="Privati"
                      checked={formData.tipiClientiAccettati.includes('Privati')}
                      onChange={handleChange}
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded mr-2"
                    />
                    <label className="text-sm text-gray-700" htmlFor="tipoPrivati">
                      Privati
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="tipoAziende"
                      name="tipiClienti"
                      value="Aziende"
                      checked={formData.tipiClientiAccettati.includes('Aziende')}
                      onChange={handleChange}
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded mr-2"
                    />
                    <label className="text-sm text-gray-700" htmlFor="tipoAziende">
                      Aziende
                    </label>
                  </div>
                </div>
              </div>

              {/* Accetta privati con reddito unico */}
              <div className="mb-4 flex items-center">
                <input
                  type="checkbox"
                  id="accettaPrivatiRedditoUnico"
                  name="accettaPrivatiRedditoUnico"
                  checked={formData.accettaPrivatiRedditoUnico}
                  onChange={handleChange}
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded mr-2"
                />
                <label className="text-sm text-gray-700" htmlFor="accettaPrivatiRedditoUnico">
                  Accetta privati con reddito unico
                </label>
              </div>

              {/* Garanzie richieste (checkbox) */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Garanzie Richieste
                </label>
                <div className="max-h-48 overflow-y-auto border border-gray-300 rounded-md p-3">
                  {garanzie.length > 0 ? (
                    <div className="space-y-2">
                      {garanzie.map(garanzia => (
                        <div key={garanzia.id} className="flex items-center">
                          <input
                            type="checkbox"
                            id={`garanzia-${garanzia.id}`}
                            name="garanzia"
                            value={garanzia.id}
                            checked={formData.idGaranzie.includes(garanzia.id)}
                  onChange={handleChange}
                            className="h-4 w-4 text-blue-600 border-gray-300 rounded mr-2"
                          />
                          <label className="text-sm text-gray-700" htmlFor={`garanzia-${garanzia.id}`}>
                            {garanzia.descrizione}
                          </label>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 text-center py-2">
                      Nessuna garanzia disponibile. Creale nella sezione Gestione Garanzie.
                    </p>
                  )}
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Seleziona una o più garanzie richieste dalla società di noleggio. Lascia vuoto se non sono richieste garanzie specifiche.
                </p>
              </div>

              {/* Note */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="note">
                  Note
                </label>
                <textarea
                  id="note"
                  name="note"
                  value={formData.note}
                  onChange={handleChange}
                  rows="3"
                  className="w-full p-2 border border-gray-300 rounded-md"
                  placeholder="Inserisci eventuali note"
                ></textarea>
              </div>

              {/* Attivo / Inattivo */}
              {!isNew && (
                <div className="mb-4 flex items-center">
                  <input
                    type="checkbox"
                    id="attivo"
                    name="attivo"
                    checked={formData.attivo}
                    onChange={handleChange}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded mr-2"
                  />
                  <label className="text-sm text-gray-700" htmlFor="attivo">
                    Società attiva
                  </label>
                </div>
              )}
            </form>
          </div>

          {/* Footer con bottoni */}
          <div className="px-6 py-3 bg-gray-50 flex justify-end space-x-3 rounded-b-lg">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Annulla
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-70 disabled:cursor-not-allowed flex items-center"
            >
              {loading && (
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              )}
              {isNew ? 'Crea Società' : 'Aggiorna Società'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SocietaNoleggioForm; 